// ---------------------------------------------------------------------------
// Parser for EvalHid's input series: Precipitation, ETP, Temperature,
// Pumping. Two real formats, depending on the project version (see
// CLAUDE.md, "Compatibility with v1.0 projects"):
//
// v2 (Ent_Precip.csv and similar — confirmed against a real project):
//   Fecha;<sub-basin_1>;<sub-basin_2>;...;<sub-basin_n>
//   dd/mm/yyyy;<value>;<value>;...;<value>
//   ...
// ";" separator, dd/mm/yyyy date, decimals with a dot. Columns are linked by
// NAME (SubBasin.description against the header).
//
// v1.0 (Apo_Precip.inp and similar — confirmed against a real v1.0 .ehd,
// see ../ejemplos/EvalHidResuelto.zip):
//   <PRECIPITACIONES>          (or <EVAPOTRANSPIRACIONES>/<TEMPERATURAS>)
//   <NUMEROSERIES>
//   34
//   <TITULOS>                  (not always present — ETP/Temp don't bring it)
//   "Dia"	"Mes"	"Año"	"<title 1>"	...
//   <CONFIGTABLA>
//   37
//   <SERIES>
//   1	1	1950	0.000	0.000	...
//   ...
// Tab separator, Day/Month/Year in separate columns, no reliable name header
// (titles come truncated and don't even exist in ETP/Temp) — columns are
// linked by INDEX (SubBasin.precipCol/etpCol/tempCol, 1-based: the real
// value the user confirmed was actually used in v1.0, unlike v2 where those
// columns are always 0 and unused).
//
// A real project (Turia) brings ~25,500 daily rows x 51 columns per v2 file
// (17-25 MB) — stored in columns (one Float64Array per sub-basin) instead
// of an array of objects to avoid spiking memory use, and it's worth
// calling from a Web Worker so it doesn't block the UI with real projects.
// ---------------------------------------------------------------------------

import { TranslatableError } from "../i18n/translate.ts";
import {
  type SeriesScale,
  findScaleDateGaps,
  formatScaleDate,
  parseScaleDateField,
} from "./seriesScale.ts";
import type { SubBasin } from "./types.ts";

export interface DailySeries {
  /** ISO dates (yyyy-mm-dd), one per row, in the file's order. */
  dates: string[];
  /**
   * v2: column name (sub-basin code), exactly as the CSV header has it.
   * v1.0: 1-based column index as a string ("1", "2"...) — no reliable
   * name, see file header. The name is only used once, when linking the
   * series (see resolveIndicesByName) — reading always goes through the
   * index already persisted in SubBasin.precipCol/etpCol/tempCol or
   * Aquifer.pumpingCol.
   */
  columns: string[];
  /** values[i] is the full series for columns[i], aligned with `dates`. */
  values: Float64Array[];
}

/**
 * The sub-basin/aquifer has its own column in `series`, by the
 * already-persisted index (0 = unlinked) — see resolveIndicesByName below.
 * Single source of truth for "does this column exist": validations.ts and
 * subBasinState.ts both used to carry their own byte-for-byte copy of this
 * check, and engine.ts's `basinColumn` a third, subtly different one against
 * `values.length` instead of `columns.length` — three independent checks
 * that could silently diverge if a parser edge case ever produced a
 * `DailySeries` where `columns` and `values` don't have the same length.
 * Checking only `columns.length` here (not `values.length` too) is
 * deliberate: `columns`/`values` are always built together, in the same
 * parsing pass, so they're never expected to disagree — a defensive check
 * against both would just hide that invariant breaking instead of matching
 * engine.ts's actual array access (`values[colIndex - 1]`).
 */
export function hasColumnAt(series: DailySeries | null, colIndex: number): boolean {
  return !!series && colIndex > 0 && colIndex - 1 < series.columns.length;
}

/**
 * Whether `series`'s column at `colIndex` has an invalid/empty (`NaN`) cell
 * anywhere within `range` — or anywhere in the whole series, if `range` is
 * omitted. `true` (has a gap) also when the column doesn't exist at all, or
 * when `range` isn't found within `series.dates` at all (e.g. a bulk
 * "Ampliar" that never actually grew the shared date axis for this
 * sub-basin) — both are exactly as "still needs completing" as a real gap
 * inside an existing range.
 *
 * Built for the exact real incident that motivated it: a bulk "Ampliar" on
 * a huge project (Duero, 1728 sub-basins) can leave some sub-basins with
 * the shared date axis grown but their OWN column still NaN-filled across
 * the newly-added range (`extendSeriesRange`'s own documented behavior for
 * columns not part of the batch that just applied) — `hasColumnAt` alone
 * says "yes, there's a column" and misses this entirely, since the column
 * genuinely exists, it's just incomplete.
 */
export function hasSeriesGapInRange(
  series: DailySeries | null,
  colIndex: number,
  range?: { start: string; end: string }
): boolean {
  if (!hasColumnAt(series, colIndex)) return true;
  const col = series!.values[colIndex - 1];
  if (!range) return col.some((v) => !Number.isFinite(v));
  const indices = dateRangeIndices(series!.dates, range.start, range.end);
  if (!indices) return true;
  for (let i = indices.start; i <= indices.end; i++) {
    if (!Number.isFinite(col[i])) return true;
  }
  return false;
}

/**
 * The exact date span covering every `NaN` cell in `series`'s column at
 * `colIndex` — from the first gap date to the last, `null` if the column
 * doesn't exist or has no gap. Deliberately just the two endpoints, not
 * every disjoint gap span separately: whatever real (non-`NaN`) dates fall
 * between them get re-fetched too, but `extendSeriesRange` just patches
 * those back in with the same values they already had — cheaper than
 * tracking multiple spans for what's normally one bulk-extend failure per
 * sub-basin.
 */
function gapDateRange(
  series: DailySeries | null,
  colIndex: number
): { start: string; end: string } | null {
  if (!hasColumnAt(series, colIndex)) return null;
  const col = series!.values[colIndex - 1];
  let first = -1;
  let last = -1;
  for (let i = 0; i < col.length; i++) {
    if (!Number.isFinite(col[i])) {
      if (first === -1) first = i;
      last = i;
    }
  }
  return first === -1 ? null : { start: series!.dates[first], end: series!.dates[last] };
}

/**
 * The date range SeriesPanel.tsx's "Complete these N" shortcut needs to
 * re-fetch for `subBasin` — the union of its precipitation and ETP gaps
 * (see `gapDateRange`), since one AEMET/ROCIO fetch already brings both
 * together. `null` for a sub-basin that isn't actually in
 * `findSubBasinsWithSeriesGap`'s own result; callers are expected to have
 * already filtered to that list.
 */
export function seriesGapRange(
  subBasin: SubBasin,
  precipitation: DailySeries | null,
  etp: DailySeries | null
): { start: string; end: string } | null {
  const p = gapDateRange(precipitation, subBasin.precipCol);
  const e = gapDateRange(etp, subBasin.etpCol);
  if (!p) return e;
  if (!e) return p;
  return { start: p.start < e.start ? p.start : e.start, end: p.end > e.end ? p.end : e.end };
}

/**
 * Sub-basins whose precipitation or ETP already has its OWN column, but
 * that column has a real gap in it somewhere — SeriesPanel.tsx's "left
 * over from an interrupted bulk extend" notice. Deliberately independent
 * of calculation range/model assignment (unlike engine.ts's
 * `findTruncatedSubBasins`, built for the post-calculation warning and
 * scoped to whatever range was just calculated): a sub-basin with NO
 * column at all is a different, already-handled case
 * (`missingSeriesFor`/"Completar subcuencas que faltan"), so it's excluded
 * here rather than counted as "gapped" too — `hasColumnAt` gates each
 * check before `hasSeriesGapInRange` (which alone would call "no column"
 * a gap as well).
 */
export function findSubBasinsWithSeriesGap(
  subBasins: SubBasin[],
  precipitation: DailySeries | null,
  etp: DailySeries | null
): SubBasin[] {
  return subBasins.filter(
    (sb) =>
      (hasColumnAt(precipitation, sb.precipCol) &&
        hasSeriesGapInRange(precipitation, sb.precipCol)) ||
      (hasColumnAt(etp, sb.etpCol) && hasSeriesGapInRange(etp, sb.etpCol))
  );
}

/** The project's 4 input series (precipitation/ETP required, temperature/pumping optional) — `null` until loaded. Lives here, next to `DailySeries`, rather than inside any single UI component, since it's shared by the wizard and every editor panel that touches series. */
export interface ProjectSeries {
  precipitation: DailySeries | null;
  etp: DailySeries | null;
  temperature: DailySeries | null;
  pumping: DailySeries | null;
}

/** Whether the project's scale (EvalHidProject.seriesScale) is already committed — true as soon as ANY of the 4 series has data. Before that, it's still free to be inferred from whichever series is pasted/uploaded first (see PasteDailySeries.tsx/SeriesLoader.tsx). */
export function isSeriesScaleLocked(series: ProjectSeries): boolean {
  return !!(series.precipitation || series.etp || series.temperature || series.pumping);
}

function dateToIso(day: string, month: string, year: string): string {
  if (!day || !month || !year) {
    throw new TranslatableError("dailySeries.emptyDateFields", { day, month, year });
  }
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/**
 * Asserts `columns`/`values`' invariant (same length — see `DailySeries`'s
 * own doc comment) at the boundary where untrusted file content becomes a
 * `DailySeries` — `parseDailySeriesCsv`/`parseDailySeriesInp` already build
 * both arrays in lockstep (`values = columns.map(...)`), so this should
 * never actually trip; it exists to catch a future edit that breaks that
 * pairing, rather than letting a mismatch flow silently into engine.ts's
 * `values[colIndex - 1]` access.
 */
function buildDailySeries(dates: string[], columns: string[], values: Float64Array[]): DailySeries {
  if (columns.length !== values.length) {
    throw new Error(
      `DailySeries invariant violated: ${columns.length} columns vs ${values.length} value arrays`
    );
  }
  return { dates, columns, values };
}

function parseDailySeriesCsv(text: string, scale?: SeriesScale): DailySeries {
  const lines = text.split(/\r\n|\n/).filter((l) => l.length > 0);
  if (lines.length === 0) {
    return buildDailySeries([], [], []);
  }

  const header = lines[0].split(";");
  const columns = header.slice(1);
  const nRows = lines.length - 1;

  const dates = new Array<string>(nRows);
  const values = columns.map(() => new Float64Array(nRows));

  for (let f = 0; f < nRows; f++) {
    const fields = lines[f + 1].split(";");
    dates[f] = parseScaleDateField(fields[0], scale);
    for (let c = 0; c < columns.length; c++) {
      const raw = fields[c + 1];
      values[c][f] = raw === undefined || raw === "" ? NaN : Number(raw);
    }
  }

  return buildDailySeries(dates, columns, values);
}

/** Parses the v1.0 format (Apo_Precip.inp and similar) — see file header. */
function parseDailySeriesInp(text: string): DailySeries {
  const lines = text.split(/\r\n|\n/);

  const iNumSeries = lines.findIndex((l) => l.trim() === "<NUMEROSERIES>");
  const numSeries = iNumSeries === -1 ? 0 : Number(lines[iNumSeries + 1]?.trim());
  const iSeries = lines.findIndex((l) => l.trim() === "<SERIES>");
  if (iSeries === -1 || !Number.isFinite(numSeries) || numSeries <= 0) {
    return buildDailySeries([], [], []);
  }

  const dataRows = lines.slice(iSeries + 1).filter((l) => l.trim().length > 0);
  // No reliable column name in this format (see file header) — the 1-based
  // index is kept as the identifier; SubBasin.precipCol/etpCol/tempCol
  // already bring that same real index from the v1.0 .ehd (see ehd.ts).
  const columns = Array.from({ length: numSeries }, (_, i) => String(i + 1));
  const dates = new Array<string>(dataRows.length);
  const values = columns.map(() => new Float64Array(dataRows.length));

  for (let f = 0; f < dataRows.length; f++) {
    const fields = dataRows[f].split("\t");
    dates[f] = dateToIso(fields[0], fields[1], fields[2]);
    for (let c = 0; c < numSeries; c++) {
      const raw = fields[c + 3];
      values[c][f] = raw === undefined || raw === "" ? NaN : Number(raw);
    }
  }

  return buildDailySeries(dates, columns, values);
}

/**
 * Parses the (already decoded to text) content of an EvalHid daily series,
 * v2 or v1.0 (see file header). `scale`: the project's already-declared
 * `seriesScale` (dailySeries.ts's own scale, not the file's — a project
 * that's already loaded data has to stay consistent), or omitted when
 * there's nothing to conform to yet (opening a project/CSV folder for the
 * very first time) — see `parseScaleDateField` (seriesScale.ts) for what
 * changes between the two. The v1.0 legacy format doesn't take a scale: it
 * already parses a monthly file correctly as-is (day/month/year always
 * arrive as 3 separate tab fields, and a monthly row's day is just "01").
 */
export function parseDailySeries(text: string, scale?: SeriesScale): DailySeries {
  return text.trimStart().startsWith("<")
    ? parseDailySeriesInp(text)
    : parseDailySeriesCsv(text, scale);
}

/** Column index by sub-basin name, to avoid scanning `columns` on every lookup. */
export function columnIndex(series: DailySeries): Map<string, number> {
  const index = new Map<string, number>();
  series.columns.forEach((name, i) => index.set(name, i));
  return index;
}

/**
 * A series' column names, without the trailing "_" that EvalHid adds to the
 * header (verified against a real project: columns link with
 * `SubBasin.description` by removing that suffix — see engine/engine.ts).
 * Shared between the engine, validations and the map (to know which
 * sub-basins are left without their own series).
 */
export function seriesColumnNames(series: DailySeries | null): Set<string> {
  return new Set((series?.columns ?? []).map((c) => c.replace(/_$/, "")));
}

/**
 * Resolves by NAME, all at once, the index (1-based, 0 if there's no column
 * with that name) of each entity in `series`, and applies it with
 * `withIndex`. The single place this logic lives — called when linking a
 * series for the first time (opening the project, uploading a CSV, or
 * pasting a single series — see project.ts/Step5Series.tsx/
 * PasteDailySeries.tsx), never when reading: once resolved, the index
 * persisted on the entity itself (`SubBasin.precipCol/etpCol/tempCol`,
 * `Aquifer.pumpingCol`) is the real link, immune to the entity being
 * renamed afterward.
 */
export function resolveIndicesByName<T>(
  entities: T[],
  series: DailySeries,
  nameOf: (entity: T) => string,
  withIndex: (entity: T, index: number) => T
): T[] {
  // Built once instead of re-scanning `series.columns` (with a regex
  // allocation per comparison) for every entity — real gap found by
  // deep-review (2026-08-29): O(entities × columns) at Duero scale (1728
  // sub-basins, up to that many columns) made this a real one-time cost at
  // project-open time. Registers BOTH the raw name and the de-suffixed one
  // per column, "first column wins" for each key (never overwriting) — the
  // same tie-break `findIndex`'s single combined `c === name ||
  // c.replace(...) === name` predicate already gave: the first column
  // index that matches EITHER form, not "first exact match, else first
  // de-suffixed match" (see the regression test for the case this
  // distinction actually matters).
  const indexByName = new Map<string, number>();
  series.columns.forEach((c, i) => {
    if (!indexByName.has(c)) indexByName.set(c, i);
    const stripped = c.replace(/_$/, "");
    if (!indexByName.has(stripped)) indexByName.set(stripped, i);
  });
  return entities.map((e) => {
    const i = indexByName.get(nameOf(e));
    return withIndex(e, i === undefined ? 0 : i + 1);
  });
}

// ---------------------------------------------------------------------------
// Paste a single sub-basin/aquifer's series from Excel (EditSubBasinDialog.tsx/
// EditAquiferDialog, via PasteDailySeries.tsx) — two tab-separated columns,
// date (dd/mm/yyyy) and value, exactly as copied from a spreadsheet with its
// own date column.
// ---------------------------------------------------------------------------

/** Result of parsing text pasted from a spreadsheet: a single column, without a name yet. */
interface PastedSeries {
  dates: string[];
  values: number[];
}

/** Parses text pasted with two tab-separated columns (date\tvalue, one row per line), in the project's own `scale` — throws with a clear message on the first invalid line. `scale` omitted (project's scale not decided yet — no series loaded at all): each date is read tolerantly (see `parseScaleDateField`), letting the caller infer the real scale afterward from the resulting dates (`detectScale`). */
export function parsePastedSeries(text: string, scale?: SeriesScale): PastedSeries {
  const lines = text
    .trim()
    .split(/\r\n|\n/)
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) throw new TranslatableError("dailySeries.noPastedData");

  const dates: string[] = [];
  const values: number[] = [];
  for (const [i, line] of lines.entries()) {
    const fields = line.split("\t");
    if (fields.length < 2) {
      throw new TranslatableError("dailySeries.lineInvalidFormat", { n: i + 1, line });
    }
    const [dateText, valueText] = fields;
    const isoDate = parseScaleDateField(dateText.trim(), scale);
    const value = Number(valueText.trim().replace(",", "."));
    if (Number.isNaN(value)) {
      throw new TranslatableError("dailySeries.lineInvalidValue", { n: i + 1, value: valueText });
    }
    dates.push(isoDate);
    values.push(value);
  }
  return { dates, values };
}

/** Formats one column as tab-separated `date\tvalue` text (dd/mm/yyyy or mm/yyyy, matching `scale`), the exact shape `parsePastedSeries` accepts for that scale — for "Copy" (PasteDailySeries.tsx), so it can be pasted into a spreadsheet (or back into the app). Rows with no value (NaN) are left blank rather than writing "NaN", which `parsePastedSeries` wouldn't accept if pasted back as-is. */
export function formatSeriesForClipboard(
  dates: string[],
  values: Float64Array,
  scale: SeriesScale
): string {
  return dates
    .map((isoDate, i) => {
      const value = values[i];
      return `${formatScaleDate(isoDate, scale)}\t${Number.isNaN(value) ? "" : value}`;
    })
    .join("\n");
}

/** `insertColumnInSeries`/`extendSeriesRange`: the resulting series, and the (1-based) index where the inserted/replaced/extended column ended up — to persist the link on the entity (see resolveIndicesByName). */
interface InsertColumnResult {
  series: DailySeries;
  index: number;
}

/**
 * Inserts or replaces, within a project series, the column for ONE
 * sub-basin/aquifer — from what was pasted from Excel (`parsePastedSeries`).
 * If the series already existed, the pasted dates must match its own
 * EXACTLY (same number of rows, same order): if not, it throws instead of
 * silently mixing two different date ranges (an explicit decision — the
 * alternative, extending the whole series with new dates and a gap in the
 * rest of the columns, was ruled out due to the risk of series with silent
 * gaps). If the series didn't exist yet, the pasted one creates it from
 * scratch, with just this one column — the rest gets filled in by pasting
 * more columns or uploading the full file from "Input series".
 *
 * `extendSeriesRange` below is the one deliberate exception to "no silent
 * gaps": AEMET's own "extend the date range" (AemetBulkFetchDialog.tsx)
 * needs exactly the operation this function refuses, and the gap it leaves
 * in every OTHER column is a visible one (surfaced by the existing
 * notices/report, same tolerance `SeriesLoader` already has for a CSV that
 * doesn't cover every sub-basin) — approved specifically for that one use.
 */
export function insertColumnInSeries(
  existingSeries: DailySeries | null,
  columnName: string,
  pasted: PastedSeries
): InsertColumnResult {
  if (!existingSeries) {
    return {
      series: {
        dates: pasted.dates,
        columns: [columnName],
        values: [Float64Array.from(pasted.values)],
      },
      index: 1,
    };
  }
  const sameDates =
    pasted.dates.length === existingSeries.dates.length &&
    pasted.dates.every((f, i) => f === existingSeries.dates[i]);
  if (!sameDates) {
    throw new TranslatableError("dailySeries.datesDoNotMatch", {
      n: pasted.dates.length,
      total: existingSeries.dates.length,
      from: existingSeries.dates[0],
      to: existingSeries.dates[existingSeries.dates.length - 1],
    });
  }

  const i = existingSeries.columns.findIndex(
    (c) => c === columnName || c.replace(/_$/, "") === columnName
  );
  const newColumn = Float64Array.from(pasted.values);
  if (i === -1) {
    return {
      series: {
        ...existingSeries,
        columns: [...existingSeries.columns, columnName],
        values: [...existingSeries.values, newColumn],
      },
      index: existingSeries.columns.length + 1,
    };
  }
  const values = existingSeries.values.slice();
  values[i] = newColumn;
  return { series: { ...existingSeries, values }, index: i + 1 };
}

/**
 * Overwrites ONE cell (`dateIndex` into `series.dates`, `colIndex` the same
 * 1-based column index every other function here uses) with `value` —
 * copy-on-write for both the outer `values` array and the touched column's
 * own `Float64Array`, so every OTHER column keeps its exact prior array
 * reference. `PasteDailySeries.tsx`'s table view uses this to edit a single
 * day's value directly, instead of copying the whole series out, fixing one
 * row by hand, and pasting the entire thing back in through
 * `insertColumnInSeries` above.
 *
 * Deliberately indexes by `colIndex`, NOT by column name (unlike
 * `patchColumnValues` below) — real bug found live: a sub-basin's
 * `description` (what `PasteDailySeries.tsx` had been passing as a
 * "columnName") doesn't always match the column's REAL name in the loaded
 * series (e.g. a MITECO import, or a CSV with its own header text) — a
 * name-based lookup there silently fails to find the column at all
 * (`patchColumnValues` returns the series UNCHANGED, no error), so an edit
 * would look successful in the input's own local echo but never actually
 * reach the series. `colIndex` is already the caller's own trusted,
 * resolved identifier (`SubBasin.precipCol` etc.) — the same one
 * `chartData`/`stats` already index by in that same file — so this can't
 * suffer that mismatch. `value` may be `NaN` — same "a real gap, not
 * zero" convention `parseAemetNumber` uses elsewhere; clearing a cell in
 * the table is how a user marks a day back to "no data".
 */
export function updateSeriesValue(
  series: DailySeries,
  colIndex: number,
  dateIndex: number,
  value: number
): DailySeries {
  const values = series.values.slice();
  const column = values[colIndex - 1].slice();
  column[dateIndex] = value;
  values[colIndex - 1] = column;
  return { ...series, values };
}

/** `insertColumnsInSeries`'s own result — `indices` keyed by the SAME `columnName` each entry was passed with, 1-based, same meaning as `InsertColumnResult.index`. */
interface InsertColumnsResult {
  series: DailySeries;
  indices: Map<string, number>;
}

/**
 * Batched form of `insertColumnInSeries` — inserts/replaces MANY columns at
 * once, in a single `columns`/`values` array rebuild instead of one rebuild
 * per column. Real incident (2026-08-25): a large MITECO+ROCIO import
 * (Duero: 1728 sub-basins) called `insertColumnInSeries` once per sub-basin
 * as each one's download finished — appending to a JS array via spread
 * (`[...existingSeries.columns, columnName]`) is O(current length), so 1728
 * sequential appends cost O(N²) overall (~3M element copies for the columns
 * array alone, ×3 series — precipitation/temperature/ETP), all on the main
 * thread, once per sub-basin: real measured impact, ~3.7s of blocked main
 * thread and frame gaps over a second long in the first 30s of a real
 * import. This does the equivalent work in ONE O(N) pass over `entries`
 * (aemetJobStore.ts's `flushApplies` buffers a burst of completed downloads
 * and calls this once per flush instead of once per sub-basin).
 *
 * Same "exact date match or throw" contract as `insertColumnInSeries` — ALL
 * entries must share the exact same `dates` (true by construction for a
 * batch built from jobs in the same MITECO/ROCIO import, which all target
 * the same forced range — see `rangeInfoForJob`). A column name already
 * present in `existingSeries` is replaced in place (same "trailing `_`
 * stripped" tolerant match); a new one is appended, in `entries`' own order.
 * Two entries with the SAME column name (shouldn't happen — one entry per
 * sub-basin, sub-basin names are what `applyBatchToProject` uses) resolve to
 * whichever comes last in `entries`, same "last write wins" a naive loop of
 * `insertColumnInSeries` calls would also give.
 */
export function insertColumnsInSeries(
  existingSeries: DailySeries | null,
  dates: string[],
  entries: { columnName: string; values: number[] }[]
): InsertColumnsResult {
  if (existingSeries) {
    const sameDates =
      dates.length === existingSeries.dates.length &&
      dates.every((f, i) => f === existingSeries.dates[i]);
    if (!sameDates) {
      throw new TranslatableError("dailySeries.datesDoNotMatch", {
        n: dates.length,
        total: existingSeries.dates.length,
        from: existingSeries.dates[0],
        to: existingSeries.dates[existingSeries.dates.length - 1],
      });
    }
  }

  const columns = existingSeries ? existingSeries.columns.slice() : [];
  const values = existingSeries ? existingSeries.values.slice() : [];
  // Built once (O(existing columns)), reused for every entry — same lookup
  // `insertColumnInSeries` does per call, but paid only once here.
  const indexByName = new Map<string, number>();
  columns.forEach((c, i) => {
    indexByName.set(c, i);
    const stripped = c.replace(/_$/, "");
    if (stripped !== c) indexByName.set(stripped, i);
  });

  const indices = new Map<string, number>();
  for (const entry of entries) {
    const newColumn = Float64Array.from(entry.values);
    const existingIndex = indexByName.get(entry.columnName);
    if (existingIndex !== undefined) {
      values[existingIndex] = newColumn;
      indices.set(entry.columnName, existingIndex + 1);
    } else {
      const newIndex = columns.length;
      columns.push(entry.columnName);
      values.push(newColumn);
      indexByName.set(entry.columnName, newIndex);
      indices.set(entry.columnName, newIndex + 1);
    }
  }

  return { series: { dates: existingSeries?.dates ?? dates, columns, values }, indices };
}

/**
 * Overwrites specific dates' values in an already-existing column, found by
 * name (same tolerant match as `insertColumnInSeries` — a trailing "_"
 * stripped) — a no-op if it isn't found. Used by AEMET's "retry failed
 * windows" (aemetRetry.ts) to patch just the retried dates into a series
 * that already has every other date filled in, instead of
 * `insertColumnInSeries`'s "replace the whole column" (which would need
 * every OTHER date resupplied too, and this never has it).
 */
export function patchColumnValues(
  series: DailySeries,
  columnName: string,
  updates: Map<string, number>
): DailySeries {
  const colIndex = series.columns.findIndex(
    (c) => c === columnName || c.replace(/_$/, "") === columnName
  );
  if (colIndex === -1) return series;
  const dateIndex = new Map(series.dates.map((d, i) => [d, i]));
  const newColumn = Float64Array.from(series.values[colIndex]);
  for (const [date, value] of updates) {
    const i = dateIndex.get(date);
    if (i !== undefined) newColumn[i] = value;
  }
  const values = series.values.slice();
  values[colIndex] = newColumn;
  return { ...series, values };
}

/**
 * Grows `series`'s date axis with `newDates`/`newValues` for `columnName` —
 * unlike `insertColumnInSeries`, does NOT require the dates to match the
 * existing axis; instead extends it (earlier or later, whichever `newDates`
 * turns out to be — AemetBulkFetchDialog.tsx is the one that keeps the
 * window contiguous with the existing range, this function doesn't assume
 * that beyond picking the extension side from `newDates[0]` vs the
 * existing first date). Every OTHER existing column gets NaN over the
 * newly-added dates — a real, visible gap (see this file's own note above
 * on why this is the one exception to `insertColumnInSeries`'s stricter
 * contract).
 *
 * Handles overlap with the existing axis too (not just pure extension):
 * when two sub-basins are being extended with the SAME new window in one
 * batch, whichever's `applyResult` runs first (aemetJobStore.ts) grows the
 * axis for everyone — the second one to apply would otherwise see its own
 * "new" dates already present. Dates already on the axis are patched
 * in-place (same as `patchColumnValues`); only genuinely new ones are
 * appended.
 */
export function extendSeriesRange(
  existingSeries: DailySeries | null,
  columnName: string,
  newDates: string[],
  newValues: number[]
): InsertColumnResult {
  if (!existingSeries) {
    return {
      series: { dates: newDates, columns: [columnName], values: [Float64Array.from(newValues)] },
      index: 1,
    };
  }
  if (newDates.length === 0) {
    const existingIndex = existingSeries.columns.findIndex(
      (c) => c === columnName || c.replace(/_$/, "") === columnName
    );
    return { series: existingSeries, index: existingIndex + 1 };
  }

  const existingDateSet = new Set(existingSeries.dates);
  const appendDates: string[] = [];
  const appendValues: number[] = [];
  const patch = new Map<string, number>();
  newDates.forEach((date, i) => {
    if (existingDateSet.has(date)) patch.set(date, newValues[i]);
    else {
      appendDates.push(date);
      appendValues.push(newValues[i]);
    }
  });

  const colIndex = existingSeries.columns.findIndex(
    (c) => c === columnName || c.replace(/_$/, "") === columnName
  );
  const series =
    patch.size > 0 && colIndex !== -1
      ? patchColumnValues(existingSeries, columnName, patch)
      : existingSeries;

  if (appendDates.length === 0) {
    if (colIndex !== -1) return { series, index: colIndex + 1 };
    // The target sub-basin had no column at all — add one, real values over
    // the (already-on-the-axis) patched dates, NaN elsewhere.
    const column = new Float64Array(series.dates.length).fill(NaN);
    const dateIndex = new Map(series.dates.map((d, i) => [d, i]));
    patch.forEach((value, date) => {
      const i = dateIndex.get(date);
      if (i !== undefined) column[i] = value;
    });
    return {
      series: {
        ...series,
        columns: [...series.columns, columnName],
        values: [...series.values, column],
      },
      index: series.columns.length + 1,
    };
  }

  const before = appendDates[0] < series.dates[0];
  const dates = before ? [...appendDates, ...series.dates] : [...series.dates, ...appendDates];
  const newColumn = Float64Array.from(appendValues);
  const gap = new Float64Array(appendDates.length).fill(NaN);

  function extend(oldColumn: Float64Array, extension: Float64Array): Float64Array {
    const combined = new Float64Array(oldColumn.length + extension.length);
    if (before) {
      combined.set(extension, 0);
      combined.set(oldColumn, extension.length);
    } else {
      combined.set(oldColumn, 0);
      combined.set(extension, oldColumn.length);
    }
    return combined;
  }

  const values = series.values.map((col, idx) => extend(col, idx === colIndex ? newColumn : gap));

  if (colIndex !== -1) {
    return { series: { dates, columns: series.columns, values }, index: colIndex + 1 };
  }
  const oldGap = new Float64Array(series.dates.length).fill(NaN);
  const fullNewColumn = extend(oldGap, newColumn);
  return {
    series: { dates, columns: [...series.columns, columnName], values: [...values, fullNewColumn] },
    index: values.length + 1,
  };
}

/**
 * Permanently removes the column at (1-based) `index` from `series` — for
 * "Delete series" (PasteDailySeries.tsx via deleteSubBasinSeriesColumn/
 * deleteAquiferPumpingColumn, projectEntities.ts), which only calls this when no
 * OTHER entity still points at that same index (checked by the caller — a
 * shared index, possible in v1.0 projects linked by index, only gets
 * unlinked there, never physically removed). Returns null if it was the
 * series' only column — nothing left to keep.
 */
export function removeColumnFromSeries(series: DailySeries, index: number): DailySeries | null {
  if (series.columns.length <= 1) return null;
  const i = index - 1;
  return {
    dates: series.dates,
    columns: series.columns.filter((_, j) => j !== i),
    values: series.values.filter((_, j) => j !== i),
  };
}

/**
 * Reassembles several batches (all cut from the SAME original series, all
 * sharing the exact `dates` axis already, unlike `insertColumnsInSeries`'s
 * own merge, which has to reconcile possibly-mismatched date ranges) back
 * into one combined series, in the batches' own order — the inverse of
 * `seriesBatching.ts`'s `chunkSeriesColumns`. `dates` passed explicitly rather than
 * read off `batches[0]` so an all-empty `batches` array (a series with no
 * columns at all, e.g. `pumping` on most projects) still returns a valid
 * empty series instead of throwing. Takes just `columns`/`values` (not a
 * full `DailySeries`) so a caller that never carried a per-batch `dates` in
 * the first place — exportProjectWorker.ts's own batch messages, which
 * send `dates` once up front instead of repeating it per batch — can pass
 * its batches straight through.
 */
export function concatSeriesBatches(
  dates: string[],
  batches: readonly Pick<DailySeries, "columns" | "values">[]
): DailySeries {
  return {
    dates,
    columns: batches.flatMap((b) => b.columns),
    values: batches.flatMap((b) => b.values),
  };
}

// ---------------------------------------------------------------------------
// Column statistics — for the summary shown next to PasteDailySeries.tsx's
// chart (date range, gaps, mean/max/min).
// ---------------------------------------------------------------------------

interface SeriesStats {
  startDate: string;
  endDate: string;
  /** Total rows in the range (with or without a value). */
  rowCount: number;
  /** Rows with a real value (not NaN) — `rowCount - valueCount` is what's left to fill in. */
  valueCount: number;
  /** Calendar steps missing from the `dates` sequence, at the series' own scale (not VALUE gaps, those are already counted by `rowCount - valueCount`). */
  dateGapCount: number;
  mean: number | null;
  max: number | null;
  min: number | null;
}

const DATE_LIST_LIMIT = 10;

/** Joins a list of dates for a user-facing message, capped so a series with hundreds of gaps/invalid cells doesn't produce an unreadable wall of dates — shared by `validations.ts` (date-gap messages) and `engine.ts`/`useCalculation.ts` (invalid-cell messages). */
export function formatDateList(dates: string[]): string {
  if (dates.length <= DATE_LIST_LIMIT) return dates.join(", ");
  const shown = dates.slice(0, DATE_LIST_LIMIT).join(", ");
  return `${shown}… (+${dates.length - DATE_LIST_LIMIT})`;
}

/**
 * Basic statistics for a `DailySeries` column. `values` must already come
 * aligned with `dates` (same index), like any column of `DailySeries.values`.
 */
export function calculateSeriesStats(
  dates: string[],
  values: Float64Array,
  scale: SeriesScale
): SeriesStats | null {
  if (dates.length === 0) return null;

  const dateGapCount = findScaleDateGaps(dates, scale).length;

  let sum = 0;
  let valueCount = 0;
  let max = -Infinity;
  let min = Infinity;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (Number.isNaN(v)) continue;
    sum += v;
    valueCount++;
    if (v > max) max = v;
    if (v < min) min = v;
  }

  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1],
    rowCount: dates.length,
    valueCount,
    dateGapCount,
    mean: valueCount > 0 ? sum / valueCount : null,
    max: valueCount > 0 ? max : null,
    min: valueCount > 0 ? min : null,
  };
}

// ---------------------------------------------------------------------------
// Date-range restriction — for CalculationOptions.startDate/endDate
// (useCalculation.ts, App.tsx): the real engine DOES read Fecha_inicio/
// Fecha_fin (see calculationOptions.ts's own header, unlike the two aquifer
// checkboxes next to it), so a calculation confirmed with a narrower range
// than the loaded precipitation's full one needs to actually run over just
// that window.
// ---------------------------------------------------------------------------

/**
 * Locates `[startDate, endDate]` (inclusive) within `dates` as a 0-based
 * index range. `null` if either date isn't present, or if they're out of
 * order — shouldn't happen in practice since CalculationOptionsDialog.tsx
 * only lets the user pick dates that are already within the loaded
 * precipitation's own range, but callers fall back to the untrimmed series
 * in that case rather than throwing.
 */
export function dateRangeIndices(
  dates: string[],
  startDate: string,
  endDate: string
): { start: number; end: number } | null {
  const start = dates.indexOf(startDate);
  const end = dates.indexOf(endDate);
  if (start === -1 || end === -1 || end < start) return null;
  return { start, end };
}

/**
 * Restricts every column of `series` to the given inclusive index range.
 * The range is always computed once from the precipitation series' own
 * dates (`dateRangeIndices`) and then applied identically to the ETP/
 * temperature series too, rather than each looking up `startDate`/`endDate`
 * in its own `dates` array — the engine already assumes every input series
 * is index-aligned to the same calendar (engine.ts's `basinColumn` reads
 * precipitation/ETP/temperature by the same row index, with no date lookup
 * of its own), so slicing by a shared index range preserves that alignment
 * instead of re-deriving it per series and risking a length mismatch if one
 * series' `dates` don't line up exactly with precipitation's.
 */
export function trimSeriesByIndexRange(
  series: DailySeries,
  range: { start: number; end: number }
): DailySeries {
  return {
    dates: series.dates.slice(range.start, range.end + 1),
    columns: series.columns,
    values: series.values.map((column) => column.slice(range.start, range.end + 1)),
  };
}
